import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { purgeLegacyAuthStorage, setAuthToken, setStoredTenantId, clearStoredTenantId } from "@/lib/sm-api";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { TURNSTILE_SITE_KEY } from "@/lib/turnstile-public";
import { shouldSmPersistBearerInClient } from "@/lib/sm-session-bridge";
import { authService } from "@/lib/services";
import type { AuthSessionUser } from "@/lib/services/auth.service";

const ERROR_MESSAGES: Record<string, string> = {
  auth0_cancelled: "Cancelaste el proceso de autenticación.",
  state_mismatch: "Sesión de login expirada. Intentá de nuevo.",
  not_approved: "Tu email no está habilitado para acceder.",
  auth_failed: "No se pudo completar el login con Google.",
  missing_params: "Respuesta de login incompleta.",
  session_failed: "No se pudo validar la sesión.",
};

export type MerchantLoginSearch = { returnTo?: string; error?: string };

export function parseMerchantLoginSearch(s: Record<string, unknown>): MerchantLoginSearch {
  const out: MerchantLoginSearch = {};
  if (
    typeof s.returnTo === "string" &&
    s.returnTo.startsWith("/") &&
    !s.returnTo.startsWith("//")
  ) {
    out.returnTo = s.returnTo;
  }
  if (typeof s.error === "string") out.error = s.error;
  return out;
}

/** @deprecated Prefer parseMerchantLoginSearch */
export const parseLoginSearch = parseMerchantLoginSearch;

export function MerchantLoginPage({ returnTo, error: urlError }: MerchantLoginSearch) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [cfTurnstileToken, setCfTurnstileToken] = useState<string | null>(null);
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [tenantOptions, setTenantOptions] = useState<
    Array<{ id: string; name: string; slug: string }>
  >([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [googleLoginEnabled, setGoogleLoginEnabled] = useState(false);

  useEffect(() => {
    void fetch("/api/platform-flags")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { providers?: { google?: boolean } } | null) => {
        setGoogleLoginEnabled(d?.providers?.google === true);
      })
      .catch(() => setGoogleLoginEnabled(false));
  }, []);

  const isDev = import.meta.env.DEV;
  const errorMsg = urlError ? (ERROR_MESSAGES[urlError] ?? "Error al autenticar.") : null;

  const finishLogin = async (user: AuthSessionUser) => {
    if (user.tenantId) setStoredTenantId(user.tenantId);
    else clearStoredTenantId();

    if (user.role === "super_admin" || user.role === "support" || user.role === "viewer") {
      void navigate({ to: "/admin", search: {} as never });
      return;
    }
    if (user.role === "owner" && !user.tenantId) {
      void navigate({ to: "/app/onboarding" });
      return;
    }
    const safeReturn = returnTo?.startsWith("/app") ? returnTo : "/app/dashboard";
    void navigate({ to: safeReturn });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    // SCRUM-183: require Turnstile token in production
    const isProd = !import.meta.env.DEV;
    if (isProd && TURNSTILE_SITE_KEY && !cfTurnstileToken) {
      setFormError("Completá la verificación de seguridad antes de continuar.");
      return;
    }
    setBusy(true);
    try {
      const result = await authService.login(username, password, {
        tenantId: selectedTenantId || undefined,
        cfTurnstileToken: cfTurnstileToken ?? undefined,
      });
      if (!result.ok) {
        setFormError(result.error);
        return;
      }
      if ("needsTenantPick" in result.data && result.data.needsTenantPick) {
        setTenantOptions(result.data.tenants);
        setSelectedTenantId(result.data.tenants[0]?.id ?? "");
        setFormError("Elegí el negocio al que querés entrar");
        return;
      }
      const session = result.data as { user: AuthSessionUser; token: string };
      if (session.token && shouldSmPersistBearerInClient()) setAuthToken(session.token);
      else purgeLegacyAuthStorage();
      await finishLogin(session.user);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border/50 bg-card p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">SM</span>
          </div>
          <span className="font-bold text-foreground">Structura Market</span>
        </div>

        <h1 className="text-xl font-bold text-foreground">Panel de tu comercio</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Acceso para dueños y equipo. ¿Sin cuenta?{" "}
          <a href="https://structura.com.ar/soluciones" className="text-primary underline-offset-4 hover:underline">
            Registrá tu comercio
          </a>
        </p>

        <div className="mt-4 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">¿Sos cliente y querés ver tus pedidos?</p>
          <p className="mt-1">
            Entrá desde la tienda donde comprás: en su página, tocá el ícono de cuenta o{" "}
            <span className="font-medium text-foreground">Mi cuenta</span>. No uses este login.
          </p>
        </div>

        {errorMsg && (
          <Alert variant="destructive" className="mt-4 py-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">{errorMsg}</AlertDescription>
          </Alert>
        )}

        {googleLoginEnabled ? (
          <>
            <a href="/api/auth/auth0/login" className="mt-6 block">
              <Button type="button" variant="outline" className="w-full">
                Continuar con Google
              </Button>
            </a>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">o con usuario</span>
              </div>
            </div>
          </>
        ) : null}

        <form className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
          <div>
            <Label className="text-xs">Email o usuario</Label>
            <Input
              className="mt-1"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              placeholder="tu@email.com"
              required
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Usá el email con el que te registraste como comercio.
            </p>
          </div>
          {tenantOptions.length > 0 ? (
            <div>
              <Label className="text-xs">Negocio</Label>
              <select
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={selectedTenantId}
                onChange={(e) => setSelectedTenantId(e.target.value)}
                required
              >
                {tenantOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.slug})
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Contraseña</Label>
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground hover:text-primary"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
            <div className="relative mt-1">
              <Input
                type={showPassword ? "text" : "password"}
                className="pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword((v) => (v ? false : true))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {formError && (
            <Alert variant="destructive" className="py-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">{formError}</AlertDescription>
            </Alert>
          )}

          {/* SCRUM-183: Cloudflare Turnstile bot protection — se omite en dev local
              (no se exige del lado servidor, cargar el widget externo sólo agrega
              latencia sin verificar nada real en localhost). */}
          {import.meta.env.DEV ? null : TURNSTILE_SITE_KEY ? (
            <TurnstileWidget
              onToken={(token) => { setCfTurnstileToken(token); setTurnstileReady(true); }}
              onError={() => { setCfTurnstileToken(null); setTurnstileReady(false); }}
              onExpire={() => { setCfTurnstileToken(null); setTurnstileReady(false); }}
              theme="auto"
              className="my-1"
            />
          ) : (
            <p className="text-xs text-amber-600 rounded border border-amber-200 bg-amber-50 px-3 py-2">
              Verificación de seguridad no disponible. Contactá soporte.
            </p>
          )}
          <Button type="submit" className="w-full" disabled={busy || (!import.meta.env.DEV && TURNSTILE_SITE_KEY ? !turnstileReady : false)}>
            {busy ? "Entrando…" : "Entrar"}
          </Button>
        </form>

        {isDev && (
          <div className="mt-6 rounded-lg border border-dashed border-border p-3">
            <p className="text-xs text-muted-foreground mb-2 font-medium">Cuentas demo (dev)</p>
            <div className="flex flex-col gap-1.5">
              {[
                { label: "Super Admin", user: "TNAdmin", pass: "M@tilda2026TN" },
                { label: "Demos (owner)", user: "demo@structura.com.ar", pass: "demo123" },
              ].map((u) => (
                <Button
                  key={u.user}
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="text-xs justify-start"
                  onClick={() => {
                    setUsername(u.user);
                    setPassword(u.pass);
                  }}
                >
                  {u.label} — {u.user}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
